import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Snackbar,
  CssBaseline,
  IconButton,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../apiBase';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setToastSeverity('error');
      setToastMessage('Invalid reset link. Please request a new password reset.');
      setToastOpen(true);
      setTimeout(() => navigate('/forgot-password'), 2000);
    } else {
      // Token is base64 encoded, decode it for display (but send encoded to backend)
      // Actually, we should send it as-is since backend expects base64
      setToken(tokenParam);
    }
  }, [searchParams, navigate]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setToastSeverity('error');
      setToastMessage(passwordError);
      setToastOpen(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setToastSeverity('error');
      setToastMessage('Passwords do not match');
      setToastOpen(true);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: newPassword,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
        setToastSeverity('success');
        setToastMessage(data.message || 'Password reset successfully!');
        setToastOpen(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setToastSeverity('error');
        setToastMessage(data.error || 'Failed to reset password');
        setToastOpen(true);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setToastSeverity('error');
      setToastMessage('An error occurred. Please try again later.');
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8f9fa',
        }}
      >
        <CssBaseline />
        <Paper
          elevation={4}
          sx={{
            zIndex: 2,
            p: 5,
            width: '100%',
            maxWidth: '450px',
            borderRadius: '24px',
            textAlign: 'center',
            bgcolor: 'white',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#c8e6c9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Typography variant="h3" sx={{ color: '#4caf50' }}>
                ✓
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight="700" gutterBottom sx={{ color: '#2c3e50' }}>
              Password Reset Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Your password has been reset successfully. You can now login with your new password.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Redirecting to login page...
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8f9fa',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CssBaseline />

      {/* Main Card */}
      <Paper
        elevation={4}
        sx={{
          zIndex: 2,
          p: 5,
          width: '100%',
          maxWidth: '450px',
          borderRadius: '24px',
          textAlign: 'center',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <LockIcon sx={{ fontSize: 48, color: '#42a5f5', mb: 1 }} />
          <Typography variant="h4" fontWeight="700" gutterBottom sx={{ color: '#2c3e50' }}>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter your new password below
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            fullWidth
            name="newPassword"
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#f5f6fa',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Password must be at least 6 characters"
          />

          <TextField
            margin="normal"
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#f5f6fa',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={confirmPassword !== '' && newPassword !== confirmPassword}
            helperText={
              confirmPassword !== '' && newPassword !== confirmPassword
                ? 'Passwords do not match'
                : ''
            }
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !newPassword || !confirmPassword}
            sx={{
              mt: 3,
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: '#42a5f5',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#2196f3',
                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
              },
            }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </Button>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/login')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: '#42a5f5',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <ArrowBackIcon fontSize="small" />
              Back to Login
            </Link>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
